import { LocalDateTime } from "@js-joda/core";
import React, { useState } from "react";
import { API } from "../../API";
import { isLoading, Loading, loading } from "../../Loading";
import {
  CaregiverSurveyQuestionAnswersCreate,
  CaregiverSurveyQuestionWithPossibleAnswers,
} from "../../messages/CaregiverSurvey";
import {
  CaregiverSurveyQuestionAnswerId,
  CaregiverSurveyQuestionId,
} from "../../messages/ObjectIds";
import { OrientationLanguages } from "../../messages/Video";
import { getLocalStorageItem, setLocalStorageItem } from "../../storage_utils";
import { STRINGS } from "../../strings";
import { getLanguage, langMap, translate } from "../../translate";
import { fmap } from "../../utils/fmap";
import { withDefault } from "../../utils/withDefault";
import CaregiverSurveyScreen from "./CaregiverSurveyScreen";

export type SurveyState = "Welcome" | "Questions" | "Finish";

interface Props {
  onClickCaregiverSurvey: () => void;
  api: API;
  localStorageKey: string;
}

const lastTimeOpenedSurveyLocalStorageKey = "last_time_opened_survey";

const CaregiverSurveyActivity = (props: Props) => {
  const [language, setLanguage] = useState<OrientationLanguages | Loading>(loading());
  const [surveyState, setSurveyState] = useState<SurveyState>("Welcome");
  const [questions, setQuestions] = useState<
    CaregiverSurveyQuestionWithPossibleAnswers[] | Loading
  >(loading());
  const [currentQuestion, setCurrentQuestion] =
    useState<CaregiverSurveyQuestionWithPossibleAnswers | null>(null);
  const [answeredQuestions, setAnsweredQuestions] = useState<number>(0);
  const surveyPopupApprovedLocalStorageKey = props.localStorageKey + ":approved";

  const questionsPercentage = React.useMemo(() => {
    if (!isLoading(questions)) {
      return ((answeredQuestions / questions.length) * 100).toFixed(0) + "%";
    }
    return "0%";
  }, [answeredQuestions, questions]);

  const remainingQuestionsText = React.useMemo(() => {
    if (!isLoading(questions)) {
      const remainedQuestionsLength = questions.length - answeredQuestions;
      if (remainedQuestionsLength > 1) {
        return (
          remainedQuestionsLength +
          " " +
          translate(STRINGS.QUESTIONS) +
          " " +
          translate(STRINGS.REMAINED)
        );
      } else {
        return (
          remainedQuestionsLength +
          " " +
          translate(STRINGS.QUESTION) +
          " " +
          translate(STRINGS.REMAINED)
        );
      }
    }
    return null;
  }, [answeredQuestions, questions]);

  const handleClickStartCaregiverSurvey = React.useCallback(() => {
    if (isLoading(questions)) {
      return;
    }
    const firstQuestion = questions.find((q) => q.order === 1);
    if (firstQuestion !== undefined) {
      setCurrentQuestion(firstQuestion);
      setSurveyState("Questions");
    }
  }, [questions]);

  const handleClickFinishCaregiverSurvey = React.useCallback(async () => {
    await setLocalStorageItem(surveyPopupApprovedLocalStorageKey, "APPROVED");
    props.onClickCaregiverSurvey.call(null);
  }, [props.onClickCaregiverSurvey, surveyPopupApprovedLocalStorageKey]);

  const handleCaregiverSurveyState = React.useCallback(() => {
    if (isLoading(questions) || currentQuestion === null) {
      return;
    }

    const nextQuestion = questions.find((q) => q.order === currentQuestion.order + 1);

    if (nextQuestion === undefined) {
      setSurveyState("Finish");
    } else {
      setCurrentQuestion(nextQuestion);
    }
  }, [currentQuestion, questions]);

  const onSubmitAnswer = React.useCallback(
    async (questionid: CaregiverSurveyQuestionId, answerIds: CaregiverSurveyQuestionAnswerId[]) => {
      const caregiverSurveyQuestionAnswerCreate: CaregiverSurveyQuestionAnswersCreate = {
        answerIds: answerIds,
      };

      await props.api.submitCaregiverSurveyAnswer(questionid, caregiverSurveyQuestionAnswerCreate);
      setAnsweredQuestions(answeredQuestions + 1);
      handleCaregiverSurveyState();
    },
    [answeredQuestions, handleCaregiverSurveyState, props.api]
  );

  React.useEffect(() => {
    getLanguage().then((clientLanguage) => {
      const selectedLanguage = withDefault(langMap[clientLanguage], "English");
      setLanguage(selectedLanguage);
    });
  }, []);

  const getMedflytSurveyPopup = React.useCallback(async () => {
    const medflySurveyPopup = await getLocalStorageItem(surveyPopupApprovedLocalStorageKey);
    return medflySurveyPopup;
  }, [surveyPopupApprovedLocalStorageKey]);

  const hasAlreadyOpenedTheSurveyLately = React.useCallback(async () => {
    const timestamp = await getLocalStorageItem(lastTimeOpenedSurveyLocalStorageKey).then((x) =>
      fmap(x, LocalDateTime.parse)
    );

    if (timestamp === null) {
      return false;
    }

    return timestamp.plusHours(20).isAfter(LocalDateTime.now());
  }, []);

  React.useEffect(() => {
    let mounted = true;
    async function medflytSurveyPopup() {
      if (mounted) {
        const medflySurveyPopupValue = await getMedflytSurveyPopup();
        const hasAlreadyOpenedLately = await hasAlreadyOpenedTheSurveyLately();
        if (medflySurveyPopupValue === "APPROVED" || hasAlreadyOpenedLately) {
          props.onClickCaregiverSurvey.call(null);
          return;
        }
      }
      props.api.getCaregiverSurveyQuestions().then((response) => {
        if (response.questions.length === 0) {
          props.onClickCaregiverSurvey.call(null);
        }
        if (mounted) {
          setQuestions(response.questions);
          setLocalStorageItem(lastTimeOpenedSurveyLocalStorageKey, LocalDateTime.now().toString());
        }
      });
    }
    medflytSurveyPopup();
    return () => {
      mounted = false;
    };
  }, [
    getMedflytSurveyPopup,
    hasAlreadyOpenedTheSurveyLately,
    props.api,
    props.onClickCaregiverSurvey,
  ]);

  if (isLoading(language)) {
    return null;
  }

  return (
    <CaregiverSurveyScreen
      surveyState={surveyState}
      currentQuestion={currentQuestion}
      progressPerecentage={questionsPercentage}
      completionText={remainingQuestionsText}
      onClickStartCaregiverSurvey={handleClickStartCaregiverSurvey}
      onClickSurveyNotInterested={props.onClickCaregiverSurvey}
      onClickFinishCaregiverSurvey={handleClickFinishCaregiverSurvey}
      onSubmitAnswer={onSubmitAnswer}
      language={language}
    />
  );
};

export default React.memo(CaregiverSurveyActivity);
